import { Button, Icon } from '@lideralia/alife-uikit';
import { Card } from '@lideralia/alife-uikit/dist/atoms/Card';
import { IconCheckCircle, IconClose, IconCloseCircle } from '@lideralia/alife-uikit/dist/atoms/Icons';
import IconEditText from '@lideralia/alife-uikit/dist/atoms/Icons/IconEditText';
import IconLanguage from '@lideralia/alife-uikit/dist/atoms/Icons/IconLanguage';
import { Container, Flex } from '@lideralia/alife-uikit/dist/atoms/Layout';
import { Switch } from '@lideralia/alife-uikit/dist/atoms/Switch';
import { Text } from '@lideralia/alife-uikit/dist/atoms/Typography';
import { EditorBlockFooter } from '@lideralia/alife-uikit/dist/organisms/BiographicalEditor/EditorBlockFooter';
import { ModalLegal } from '@lideralia/alife-uikit/dist/organisms/Modals';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { requestApi2 } from '../../../../../lib/request';
import { getActualLanguage } from '../../../../common/functions/lang';
import { services } from '../../config';

const MAX_WORDS = 900;

function countWords(text) {
  if (!text) return 0;
  // Strip HTML tags and normalize whitespace to match PHP str_word_count(strip_tags())
  const stripped = text.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ');
  return stripped.trim().split(/\s+/).filter(Boolean).length;
}

export const BiographyResultScreen = () => {
  const history = useHistory();
  const { idLine, idName } = useParams();
  const lang = getActualLanguage();
  const intl = useIntl();

  const { memory } = useSelector((state) => state.biographicalEditor);
  const line = useSelector((state) => state.line);
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [originalIsPublic, setOriginalIsPublic] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [switchResetKey, setSwitchResetKey] = useState(0);

  useEffect(() => {
    if (memory?.final_text) {
      const initial = memory.final_text;
      setText(initial);
      setOriginalText(initial);
      const pub = !!memory.is_public;
      setIsPublic(pub);
      setOriginalIsPublic(pub);
    }
  }, [memory]);

  const isDirty = text !== originalText || isPublic !== originalIsPublic;

  const handlePublicToggle = useCallback((val) => {
    const publishedUuid = line?.active?.biographicalMemoryPublishedUuid;
    if (val === true && publishedUuid && publishedUuid !== memory?.uuid) {
      setShowReplaceModal(true);
    } else {
      setIsPublic(val);
    }
  }, [line?.active?.biographicalMemoryPublishedUuid, memory?.uuid]);

  const handleConfirmReplace = useCallback(() => {
    setShowReplaceModal(false);
    setIsPublic(true);
  }, []);

  const handleCancelReplace = useCallback(() => {
    setShowReplaceModal(false);
    setSwitchResetKey((k) => k + 1);
  }, []);

  const wordCount = countWords(text);
  const progressPercent = Math.min(Math.round((wordCount / MAX_WORDS) * 100), 100);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await requestApi2(services.saveText(idLine, text, isPublic, memory?.uuid));
      setIsEditing(false);
      setOriginalText(text);
      setOriginalIsPublic(isPublic);
      history.push(`/${lang}/lines/${idLine}/${idName}`);
    } finally {
      setSaving(false);
    }
  };

  // Dynamic footer height
  const [footerHeight, setFooterHeight] = useState(0);
  const observerRef = useRef(null);
  const footerRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (el) {
      const observer = new ResizeObserver(([entry]) => {
        const { height } = entry.contentRect;
        requestAnimationFrame(() => setFooterHeight(height));
      });
      observer.observe(el);
      observerRef.current = observer;
    }
  }, []);

  return (
    <>
    <Container paddingY={3} maxWidth={['400px']}>
      {/* Card */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 18,
        boxShadow: '0px 8px 18px 0px rgba(60,54,58,0.1)',
        padding: 18,
        marginBottom: 18,
      }}>
        {/* Card header: title + edit button */}
        <Flex alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
          <Text sizeText="display16" fontWeight="bold" color="black96">
            <FormattedMessage id="biographicalEditor.result.title" />
          </Text>
          {isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              aria-label={intl.formatMessage({ id: 'biographicalEditor.result.cancelEdit' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 30,
                padding: '0 6px',
                border: '1.5px solid #7F7F8A',
                borderRadius: 10,
                background: 'transparent',
                cursor: 'pointer',
                flexShrink: 0,
                color: '#7F7F8A'
              }}
            >
                <IconClose stroke="#7F7F8A" width={18} height={18} style={{ display: 'block' }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              aria-label={intl.formatMessage({ id: 'biographicalEditor.result.editText' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 30,
                padding: '0 6px',
                border: '1.5px solid #7F7F8A',
                borderRadius: 10,
                background: 'transparent',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <IconEditText stroke="#7F7F8A" width={18} height={18} style={{ display: 'block' }} />
            </button>
          )}
        </Flex>

        {/* Text display or textarea */}
        {isEditing ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={16}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            style={{
              width: '100%',
              border: '1px solid #E5E5E8',
              borderRadius: 10,
              padding: '12px 6px',
              fontSize: 16,
              fontFamily: 'inherit',
              color: '#7F7F8A',
              resize: 'vertical',
              lineHeight: '18px',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
        ) : (
          <Text
            sizeText="display13"
            color="black60"
            style={{ lineHeight: '18px', whiteSpace: 'pre-wrap', padding: '12px 6px', display: 'block' }}
          >
            {text}
          </Text>
        )}
      </div>

      {/* Publish card */}
      <Card marginY={1} padding={3}>
        <Flex alignItems="center">
          <IconLanguage stroke="#7F7F8A" width={24} height={24} style={{ flexShrink: 0 }} />
          <Text
            textAlign="left"
            marginLeft={2}
            sizeText="display13"
            color="black60"
            style={{ textTransform: 'uppercase', flex: '1' }}
          >
            <FormattedMessage id="biographicalEditor.result.publishOnWeb" />
          </Text>
          <Switch
            key={`${memory?.uuid}-${memory?.is_public}-${switchResetKey}`}
            name="is_public"
            id="is_public"
            defaultValue={!!memory?.is_public}
            sizeInput="small"
            action={handlePublicToggle}
          />
        </Flex>
      </Card>

      <div style={{ height: footerHeight }} />
    </Container>

    <div ref={footerRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}>
      <EditorBlockFooter
        currentStep={wordCount}
        totalSteps={MAX_WORDS}
        stepLabel={intl.formatMessage(
          { id: 'biographicalEditor.blockQuestion.step' },
          { current: wordCount, total: MAX_WORDS }
        )}
        blockName={intl.formatMessage({ id: 'biographicalEditor.result.wordsLabel' })}
        barPercent={progressPercent}
        saveLabel={intl.formatMessage({ id: 'biographicalEditor.result.save' })}
        backLabel={intl.formatMessage({ id: 'biographicalEditor.result.back' })}
        saveDisabled={!isDirty || saving}
        onBack={() => history.push(`/${lang}/lines/${idLine}/${idName}`)}
        onSave={handleSave}
      />
    </div>

    <ModalLegal
      isOpen={showReplaceModal}
      id="alife-replace-memory-modal"
      title={<FormattedMessage id="biographicalEditor.result.replaceModal.title" />}
      content={
        <div>
          <Text sizeText="display14" color="black60" style={{ lineHeight: '20px' }}>
            <FormattedMessage
              id="biographicalEditor.result.replaceModal.body"
            />
          </Text>
          <Flex justifyContent="flex-end" style={{ marginTop: 20, gap: 10 }}>
            <Button
                colorType="green"
                sizeButton="big"
                fullWidth
                onClick={handleConfirmReplace}
                >
                <Icon sizeIcon="display24" marginRight={2}>
                    <IconCheckCircle />
                </Icon>
                <FormattedMessage id="common.continue" />
            </Button>
            
            <Button
                colorType="red"
                fullWidth
                sizeButton="big"
                onClick={handleCancelReplace}
                >
                <Icon sizeIcon="display24" marginRight={2}>
                    <IconCloseCircle />
                </Icon>
                <FormattedMessage id="common.cancel" />
            </Button>
          </Flex>
        </div>
      }
      onModalClose={handleCancelReplace}
    />

    </>
  );
};

export default BiographyResultScreen;
